"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Briefcase, Award, TrendingUp, Users, ChartBar as BarChart } from 'lucide-react';
interface JourneyStep {
  number: string;
  phase: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
}
const steps: JourneyStep[] = [{
  number: "01",
  phase: "PHASE 01 · ANALYSIS",
  title: "Deep revenue diagnostics",
  description: "We begin by auditing your current OTA performance and market positioning. Every engagement starts with a rigorous data review — identifying untapped demand and revenue leakage points within your existing tech stack.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-sizes",
  alt: "Revenue audit analysis phase showing detailed hotel performance review"
}, {
  number: "02",
  phase: "PHASE 02 · STRATEGY",
  title: "Crafting the roadmap",
  description: "From our findings, we build a bespoke revenue strategy. We define clear pricing tiers, channel distribution tactics, and inventory management rules that align with your property's unique market segment and goals.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategy_planning_session.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategy_planning_session.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/strategy_planning_session.webp-wvc-sizes",
  alt: "Revenue strategy planning session with growth forecasting"
}, {
  number: "03",
  phase: "PHASE 03 · INTEGRATION",
  title: "Tech stack optimization",
  description: "We connect your property management system, channel manager, and booking engine directly. Clean two-way inventory sync eliminates manual extranet updates and prevents overbooking.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/tech_stack_integration.webp-wvc-sizes",
  alt: "Implementation phase with hotel tech stack configuration"
}, {
  number: "04",
  phase: "PHASE 04 · EXECUTION",
  title: "Active revenue management",
  description: "Our team takes the helm of your OTA channels. We monitor market shifts, adjust rates in real-time, and manage inventory availability to ensure your property captures every high-value booking opportunity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/active_revenue_management.webp",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/active_revenue_management.webp-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/active_revenue_management.webp-wvc-sizes",
  alt: "Optimization phase with performance monitoring"
}, {
  number: "05",
  phase: "PHASE 05 · GROWTH",
  title: "Sustainable performance",
  description: "The final stage is continuous refinement. We track your KPIs, analyze performance reports, and iterate on our strategies to ensure your hotel maintains a competitive edge and consistent revenue growth.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_performance_metrics.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_performance_metrics.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/growth_performance_metrics.png-wvc-sizes",
  alt: "Growth phase with revenue scaling results"
}];
const icons = [Briefcase, Award, TrendingUp, Users, BarChart];
function StepItem({
  step,
  index
}: {
  step: JourneyStep;
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.3,
    rootMargin: "0px 0px -10% 0px"
  });
  const IconComponent = icons[index % icons.length];
  return <div ref={ref} data-index={index} className="relative grid grid-cols-[auto_1fr] gap-6 md:gap-12 p-6 rounded-lg bg-white mb-8 last:mb-0">
      {}
      <div className="flex flex-col items-center pt-1">
        <div className={`relative w-14 h-14 flex items-center justify-center transition-all duration-700 ease-out`}>
          <IconComponent size={32} color="#0B4D9C" />
        </div>
      </div>

      {}
      <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`} style={{
      transitionDelay: "150ms"
    }}>
        <p className="text-xs tracking-[0.2em] text-secondary mb-4 font-mono uppercase">
          {step.phase}
        </p>
        <h3 className="font-sans font-semibold text-xl leading-tight text-[#0B4D9C] mb-5">
          {step.title}
        </h3>
        <p className="text-base leading-relaxed text-muted-foreground max-w-2xl mb-6">
          {step.description}
        </p>

        <div className="mt-6">
          <img src={step.image} data-wvc-srcset={step.srcset} data-wvc-sizes={step.sizes} alt={step.alt} loading="lazy" decoding="async" className="w-full max-w-[400px] h-auto rounded-lg shadow-md object-cover aspect-[16/9]" />
        </div>
      </div>
    </div>;
}
export default function ProfessionalMilestones() {
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="4431" id="professional-milestones" className="relative bg-[#F5F5F5] text-foreground py-24 md:py-32 lg:py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        {}
        <div ref={introRef} className={`text-center max-w-4xl mx-auto mb-20 md:mb-28 lg:mb-36 transition-all duration-1000 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-xs md:text-sm tracking-[0.3em] text-secondary mb-8 font-mono uppercase">
            OUR CONSULTING PROCESS
          </p>
          <h3 className="font-default text-foreground leading-tight mb-8 tracking-tight" style={{
          fontSize: "clamp(1.875rem, 4.5vw, 3.5rem)",
          fontWeight: 600
        }}>
            Our 5-Stage Commercial Optimization Process
          </h3>
          <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto">
            I work directly with you through a structured five-step process to audit your channels, eliminate commission waste, and manage room rates day by day.
          </p>
        </div>

        {}
        <div className="relative max-w-4xl mx-auto">
          {}
          <div className="absolute left-13 top-0 bottom-0 w-[2px] bg-[#0B4D9C] opacity-20" aria-hidden="true" />

          {}
          <div className="relative">
            {steps.map((step, i) => <StepItem key={step.number} step={step} index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}